import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Location, useBlocker, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { logEvent } from 'firebase/analytics';

import { BasePageContent, ShadedBox } from '../BasePage';
import { UEventFormField, useEditEventContext } from '../../contexts/EditEventContext';
import { DEFAULT_COLORS, useBackgroundContext } from '../../contexts/BackgroundContext';
import EventForm, { ErrorContainer } from '../../components/forms/EventForm';
import { useUserContext } from '../../contexts/UserContext';
import { stob64 } from '../../util/stringutils';
import { isUserHost } from '../../lib/attendance';
import { useEventCacheContext } from '../../contexts/EventCacheContext';
import { cloneEvent, EventsFilterType, IUEvent, IUEventErrors, IULocation } from '../../lib/event';
import AttendeesList from '../Event/AttendeesList';
import ShineLogo from '../../components/icons/ShineLogo';
import { startUserEvent, UserEventType } from '../../lib/performance';
import FooterLinks from '../../components/FooterLinks';
import { getAlbumForElk, getEvent } from '../../api/ElkEventService';
import { ULoginStatus } from '../../state/reducers/user';
import { isInvalidEventDuration } from '../../lib/time';
import PageContentWithActionMenu from '../../components/EventForm/PageContentWithActionMenu';
import BasePageHeader, { UPageType } from '../BasePageHeader';
import { ActionRow, EventModules, StickyFooter, StickyFooterWhiteButton } from '../../components/EventForm/Common';
import { ActionMenuTab } from '../../components/EventForm/ActionMenu';
import { useToastContext } from '../../contexts/ToastContext';
import { getAddressString } from '../../lib/location';
import { firebaseAnalytics } from '../../core/libs/Firebase';
import { CreateFlow } from '../CreateEventAction';
import EventPage from '../Event/EventPage';
import Modal from '../../components/modals/Modal';
import { useModalContext } from '../../contexts/ModalContext';
import { useAutoSaveContext } from '../../contexts/AutoSaveContext';
import { getInspirationById } from '../../lib/inspiration';
import { calculateImagePalette } from '../../lib/images';

import { logSumoEvent, stringifyError, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';
import { VerticalFullContainer } from 'Common/src/components/styled';

import { TElkFetchAlbumResponse } from 'TProtocol/prototypes/events/messages';

interface CreateEventPageState {
  fromQuickCreate?: boolean;
  imagesGenerating?: boolean;
  autoSaveId?: string;
  isEdited?: boolean;
  fromEditDirectly?: boolean;
}

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const userContext = useUserContext();
  const backgroundContext = useBackgroundContext();
  const autoSaveContext = useAutoSaveContext();
  const eventCacheContext = useEventCacheContext();
  const editEventContext = useEditEventContext();
  const toastContext = useToastContext();
  const modalContext = useModalContext();
  const { baseEventId, baseInspirationId, draftId } = useParams();
  const location = useLocation() as Location<CreateEventPageState>;

  const formRef = useRef<HTMLFormElement>(null);
  const [oldEvent, setOldEvent] = useState<IUEvent>();
  const [loginError, setLoginError] = useState<string>();
  const albumRequest = useRef<Promise<TElkFetchAlbumResponse>>();
  const pageContentRef = useRef<HTMLDivElement>(null);

  const albumId = queryParams.get('albumId');
  const event = editEventContext.event;
  const loggedIn = userContext.isLoggedIn();

  const [albumPrefillLoading, setAlbumPrefillLoading] = useState(albumId !== null);
  const [autoSaveId] = useState(location.state?.autoSaveId || queryParams.get('autosave') || uuidv4());
  const [openTab, setOpenTab] = useState<ActionMenuTab | undefined>(
    location.state?.imagesGenerating ? ActionMenuTab.IMAGE_GEN : undefined
  );
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [createDisabled, setCreateDisabled] = useState(true);
  const disableBlockerRef = useRef<boolean>(false);

  const blocker = useBlocker(() => {
    return !disableBlockerRef.current && editEventContext.hasEventChanged();
  });

  useEffect(() => {
    if (baseEventId !== undefined) {
      document.title = 'Shine Parties - Clone Party';
      logEvent(firebaseAnalytics, 'page_view', {
        page_title: 'Clone Event - Analytics'
      });
    } else {
      document.title = 'Shine Parties - Create New Party';
      logEvent(firebaseAnalytics, 'page_view', {
        page_title: 'Create Event - Analytics'
      });
    }
  }, []);

  useEffect(() => {
    if (!baseEventId &&
      !baseInspirationId &&
      !albumId &&
      !draftId &&
      (!location.state?.fromQuickCreate || event === undefined) &&
      ((loggedIn && userContext.name) || !loggedIn) &&
      !location.state?.autoSaveId &&
      !location.state?.isEdited) {
      editEventContext.createNewEvent({ ...editEventContext.eventDefaults(), id: autoSaveId });
    }
  }, [userContext.name]);

  useEffect(() => {
    if (baseEventId !== undefined) {
      void cloneBaseEvent(baseEventId);
    } else if (baseInspirationId !== undefined) {
      void createFromGallery(baseInspirationId);
    } else if (draftId !== undefined) {
      void createFromDraft(draftId);
      void checkIfDraftAlreadyCreated(draftId);
    } else if (autoSaveId !== undefined) {
      void editEventContext.unstoreEvent(true, autoSaveId);
    }
  }, [baseEventId, baseInspirationId, draftId, autoSaveId]);

  useEffect(() => {
    if (userContext.isLoggedIn() && albumId !== null && userContext.name !== '') {
      void createFromAlbum(albumId);
    } else if (userContext.loginState.status === ULoginStatus.AfterLogin) {
      userContext.finishLogin();
    }
  }, [userContext.loginState.status, userContext.name]);

  useEffect(() => {
      if (event !== undefined) {
        backgroundContext.setColors(
          {
            colors: event?.colors,
            animationUrl: event.backgroundAnimationUrl,
            animationDisabled: event.videoBackgroundDisabled
          }
        );
      }
      if (event?.photoUrl) {
        editEventContext.updateFormErrors({
          missingImage: false
        });
      }
    },
    [event?.id, event?.photoUrl, event?.colors, event?.animation, event?.backgroundAnimationUrl, event?.videoBackgroundDisabled]);

  useEffect(() => {
    if (autoSaveId !== location.state?.autoSaveId) {
      navigate(location.pathname + location.search, { state: { ...location.state, autoSaveId }, replace: true });
    }
  }, [autoSaveId]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      modalContext.show({
        contents: <Modal
          title={'Save changes to your party?'}
          primaryButtonLabel={'Yes'} secondaryButtonLabel={'No'}
          onPrimaryClick={blockerModalSave}
          onSecondaryClick={blockerModalCancel}
          onClickOutside={() => blocker.reset()}
          useSystemColors={true}/>
      })
    }
  }, [blocker.state]);

  const cloneBaseEvent = async (baseEventId: string) => {
    const oldEvent = await eventCacheContext.fetchEvent({ eventId: baseEventId });
    if (!oldEvent) {
      return;
    }
    const newEvent = cloneEvent(oldEvent);
    editEventContext.setEvent(newEvent);
    setOldEvent(oldEvent);
  };

  const createFromGallery = async (baseGalleryId: string) => {
    const inspiration = await getInspirationById(baseGalleryId);
    if (inspiration === undefined) {
      return;
    }

    const colors = await calculateImagePalette(inspiration.photoUrl);
    if (inspiration.color) {
      colors.primary = inspiration.color;
    }

    const defaults: Partial<IUEvent> = {
      ...editEventContext.eventDefaults(),
      ...inspiration,
      primaryOverrideColor: inspiration.color,
      colors,
      inspirationId: baseGalleryId,
      id: uuidv4()
    };
    if (inspiration.weekday !== undefined) {
      const d = new Date();
      d.setDate(d.getDate() + (inspiration.weekday + 7 - d.getDay()) % 7);

      defaults.date = d;
    }

    editEventContext.createNewEvent(defaults);
  };

  const createFromAlbum = async (albumId: string) => {
    try {
      if (albumRequest.current) {
        return;
      }
      albumRequest.current = getAlbumForElk(userContext, albumId);
      const { album } = await albumRequest.current;

      editEventContext.createNewEvent({
        hostedBy: userContext.name,
        title: album?.title,
        startTime: album?.startTs?.toNumber(),
        endTime: album?.endTs?.toNumber(),
        location: new IULocation({
          displayName: album?.addressInfo ? getAddressString(album.addressInfo) : '',
          address: album?.addressInfo
        }),
        existingAlbumId: albumId
      });
      setAlbumPrefillLoading(false);
    } catch (e) {
      setLoginError(`A error occurred while fetching the album: ${stringifyError(e)}`);
    }
  };

  const checkIfDraftAlreadyCreated = async (draftId: string) => {
    try {
      await getEvent(userContext, draftId, undefined, false);
      // If this succeeded, then this draft has already been converted into an event
      autoSaveContext.remove(draftId);
      editEventContext.clearEvent();
      navigate(`/event/edit/${draftId}`);
    } catch (e) {
      // This is expected.
    }
  }

  const createFromDraft = async (draftId: string) => {
    try {
      await editEventContext.unstoreOrLoadEvent(true, draftId);
    } catch (e) {
      modalContext.show({
        contents: <Modal
          title="Draft Error"
          subtitle="This draft does not exist."
          primaryButtonLabel="View my drafts"
          onPrimaryClick={() => {
            modalContext.hide();
            navigate('/events', { state: { activeTab: EventsFilterType.Drafts } });
          }}
        />
      });
    }
  };

  const validateForm = (): boolean => {
    if (!event) {
      return false;
    }

    let valid = true;
    const formErrors: IUEventErrors = {
      invalidTitle: false,
      invalidStartDate: false,
      invalidDatePair: false,
      invalidDuration: false,
      missingImage: false
    };

    if (event.title.trim() === '') {
      editEventContext.setFieldInvalid(UEventFormField.Title);
      formErrors.invalidTitle = true;
      valid = false;
    }
    if (event.photoUrl === undefined || event.photoUrl === '') {
      editEventContext.setFieldInvalid(UEventFormField.Image);
      formErrors.missingImage = true;
      valid = false;
    }
    if (event.startTime && event.startTime < Date.now()) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidStartDate = true;
      valid = false;
    }
    if (event.endTime && event.startTime > event.endTime) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidDatePair = true;
      valid = false;
    }
    if (event.endTime && isInvalidEventDuration(event.startTime, event.endTime)) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidDuration = true;
      valid = false;
    }
    if (event.location.address === undefined) {
      editEventContext.setFieldInvalid(UEventFormField.Location);
      formErrors.invalidLocation = true;
      valid = false;
    }

    editEventContext.updateFormErrors(formErrors);

    return valid;
  };

  const handleSave = async () => {
    startUserEvent(UserEventType.EventCreated);

    editEventContext.clearFieldValidity();
    const validForm = validateForm();
    const validHtmlForm = formRef.current?.reportValidity();
    if (!validForm || !validHtmlForm) {
      editEventContext.scrollToError();
      return;
    }

    if (event) {
      editEventContext.setError(undefined);
      navigate(location.pathname + location.hash, { replace: true, state: { isEdited: true } });

      event.isHost = isUserHost(event, userContext.id);
      eventCacheContext.setEvent(event);

      const encodedTitle = stob64(event?.title ?? '');

      let createFlow;
      if (baseInspirationId !== undefined) {
        createFlow = CreateFlow.GALLERY;
      } else if (baseEventId !== undefined) {
        createFlow = CreateFlow.CLONE;
      } else if (location.state?.fromQuickCreate) {
        if (location.state?.fromEditDirectly) {
          createFlow = CreateFlow.DIRECT_EDIT;
        } else {
          createFlow = CreateFlow.QUICK_CREATE;
        }
      }

      navigate(`/event/create/action/${encodedTitle}`, { state: { createFlow } });
    }
  };

  const onBackClick = (calledFromBlocker = false) => {
    editEventContext.clearEvent();

    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[CreateEventPage] back button click'
    });

    if (calledFromBlocker) {
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    } else {
      if (!userContext.isLoggedIn()) {
        navigate('/welcome');
      } else if (baseInspirationId !== undefined) {
        navigate('/instant');
      } else if (baseEventId !== undefined) {
        navigate(`/event/${baseEventId}`);
      } else {
        navigate('/events', { state: { activeTab: EventsFilterType.Drafts } });
      }
    }
  };

  const scrollToTop = () => {
    pageContentRef.current?.scrollTo(0, 0);
  };

  const onAddImage = () => {
    setOpenTab(ActionMenuTab.IMAGE_GEN);
  };

  const blockerModalSave = () => {
    modalContext.hide();
    void editEventContext.storeEvent();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const blockerModalCancel = () => {
    modalContext.hide();
    if (location.pathname.includes('draft') && editEventContext.originalEvent) {
      // User is editing an existing draft
      void autoSaveContext.save(editEventContext.originalEvent, false);
    } else if (event?.id) {
      void autoSaveContext.remove(event.id);
    }
    onBackClick(true);
  };

  const formSave = () => {
    disableBlockerRef.current = true;
    void handleSave();
  }

  const formCancel = () => {
    disableBlockerRef.current = true;
    onBackClick(false);
  }

  const onSaveDraft = () => {
    disableBlockerRef.current = true;
    navigate('/event/draft/create/action');
  };

  const handlePreviewClick = () => {
    logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[CreateEventPage] Clicked on preview'
    });

    setShowPreview(true);
  }

  if (albumPrefillLoading) {
    let content: ReactNode;
    if (userContext.loginState.status === ULoginStatus.ErrorDuringLogin) {
      content = <ShadedBox>
        <ErrorContainer>
          An error occurred while logging in: {userContext.loginState.error}
        </ErrorContainer>
      </ShadedBox>;
    } else if (loginError) {
      content = <ShadedBox>
        <ErrorContainer>
          {loginError}
        </ErrorContainer>
      </ShadedBox>;
    } else {
      content = <h1>Please wait while we prepare your party.</h1>;
    }
    // TODO: only blur when animation is picked
    return <>
      <BasePageContent $wide={true} containerRef={pageContentRef}>
        <LoginMessage>
          <ShineLogo size={300}/>
          {content}
        </LoginMessage>
      </BasePageContent>
    </>;
  } else if (showPreview) {
    return <EventPage readOnly={true} showPreview={true} setShowPreview={setShowPreview}/>
  } else {
    return <>
      <PageContentWithActionMenu event={event} isCreate={true} scrollToTop={scrollToTop} openTab={openTab}
                                 setOpenTab={setOpenTab}>
        <BasePageHeader page={UPageType.CreateEvent}/>
        <BasePageContent $wide={true} $full={true} $relative={false} $flush={true} containerRef={pageContentRef}>
          <PageContainer>
            <EventModules>
              {oldEvent ? <TopMessage>
                You are editing a copy of {oldEvent.title}. All attendees have been copied, but invites have not been
                sent
                yet.
              </TopMessage> : null}
              {event &&
                  <EventForm event={event} formRef={formRef} key={event.id}
                             onAddImage={onAddImage} setOpenTab={setOpenTab}
                             onSaveDraft={onSaveDraft}
                             setCreateDisabled={setCreateDisabled}/>}
              {event && event.isClone && <AttendeesList event={event} readOnly={true} messagesByInviteeId={new Map()}/>}
            </EventModules>
          </PageContainer>
          <FooterLinks/>
        </BasePageContent>
        <StickyFooter>
          <ActionRow $noMargins={true}>
            <StickyFooterWhiteButton type="button" onClick={formSave} disabled={createDisabled}>
              Create
            </StickyFooterWhiteButton>
            <StickyFooterWhiteButton type="button" onClick={handlePreviewClick}>
              Preview
            </StickyFooterWhiteButton>
            <StickyFooterWhiteButton type="button" onClick={formCancel}>
              Cancel
            </StickyFooterWhiteButton>
          </ActionRow>
        </StickyFooter>
        {toastContext.content}
      </PageContentWithActionMenu>
    </>;
  }
};

const TopMessage = styled(ShadedBox)`
  margin-bottom: 0;
  padding: 15px;
  background-color: #026c9caa;
  color: #FFF;
  max-width: 557px;
`;

const LoginMessage = styled(VerticalFullContainer)`
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const PageContainer = styled.div`
  margin: auto;
`;

export default CreateEventPage;
